import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle, XCircle, PaperPlaneTilt, Truck, Package, Receipt, Printer,
  ArrowsClockwise, WarningCircle, CurrencyNgn,
} from "@phosphor-icons/react";
import { api, formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { STATUS_META, PAYMENT_STAGES, formatMoney, formatDateTime, ROLE_LABEL, lineTotal } from "../lib/constants";
import { toast } from "sonner";

const STANDARD_PIPELINE = [
  { key: "pending_admin", label: "Sales → Admin" },
  { key: "pending_finance", label: "Admin → Finance" },
  { key: "payment_confirmed", label: "Finance → Admin" },
  { key: "in_warehouse", label: "Admin → Warehouse" },
  { key: "delivered", label: "Warehouse → Admin" },
  { key: "completed", label: "Done" },
];
const INSTALLMENT_PIPELINE = [
  { key: "pending_admin", label: "Sales → Admin" },
  { key: "pending_finance", label: "Admin → Finance" },
  { key: "awaiting_next_payment", label: "Loop / Final" },
  { key: "payment_confirmed", label: "Final Payment" },
  { key: "in_warehouse", label: "Warehouse" },
  { key: "delivered", label: "Delivered" },
  { key: "completed", label: "Done" },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reason, setReason] = useState("");

  // Change pop-up state
  const [changeDialog, setChangeDialog] = useState(null);
  // Refund modal state
  const [refundDialog, setRefundDialog] = useState(null);
  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editStage, setEditStage] = useState("");
  const [editChangeSummary, setEditChangeSummary] = useState("");

  const refresh = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      // Trigger change pop-up if user is in unseen_by
      if (data.unseen_by && data.unseen_by.includes(user.id) && data.last_change_summary) {
        setChangeDialog({ summary: data.last_change_summary, orderRef: data.order_id });
      }
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const acknowledgeChange = async () => {
    try {
      await api.post(`/orders/${id}/acknowledge`);
      setChangeDialog(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const callAction = async (path, requireReason, body) => {
    if (requireReason && !reason.trim()) {
      toast.error("A reason is required.");
      return;
    }
    setActionLoading(true);
    try {
      const payload = body || { reason: reason || null };
      const { data } = await api.post(`/orders/${id}/${path}`, payload);
      setOrder(data);
      setReason("");
      toast.success("Action completed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  const startEdit = () => {
    setEditItems(order.items.map((it) => ({ ...it })));
    setEditStage(order.payment_stage || "");
    setEditChangeSummary("");
    setEditing(true);
  };

  const saveEdit = async () => {
    const cleanItems = editItems
      .filter((i) => i.name && Number(i.quantity) > 0)
      .map((i) => ({
        category: i.category,
        name: i.name,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        truck_fee: Number(i.truck_fee) || 0,
      }));
    if (cleanItems.length === 0) {
      toast.error("At least one line required.");
      return;
    }
    setActionLoading(true);
    try {
      const body = { items: cleanItems };
      if (order.order_type === "installment" && editStage) body.payment_stage = editStage;
      if (editChangeSummary.trim()) body.change_summary = editChangeSummary.trim();
      const { data } = await api.patch(`/orders/${id}`, body);
      setOrder(data);
      setEditing(false);
      toast.success("Order updated. Admin & finance have been notified.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="p-10 overline">Loading…</div>;
  if (!order) return null;

  const isWarehouseView = user.role === "warehouse";
  const isOwnerSales = user.role === "sales" && order.sales_rep_id === user.id;
  const isAssignedAdmin = (user.role === "admin" || user.role === "super_admin") && (order.assigned_admin_id === user.id || user.role === "super_admin");
  const isFinance = user.role === "finance" || user.role === "super_admin";
  const isWarehouseRole = user.role === "warehouse" || user.role === "super_admin";

  const actions = [];
  if (isAssignedAdmin && order.status === "pending_admin") {
    actions.push({ label: "Approve & send to Finance", path: "admin-approve", icon: <CheckCircle size={16} weight="bold" />, primary: true });
    actions.push({ label: "Reject (return to Sales)", path: "admin-reject", icon: <XCircle size={16} weight="bold" />, requireReason: true, danger: true });
  }
  if (isOwnerSales && order.status === "cancelled_by_admin") {
    actions.push({ label: "Resubmit to admin", path: "sales-resubmit", icon: <PaperPlaneTilt size={16} weight="bold" />, primary: true });
  }
  if (isFinance && order.status === "pending_finance") {
    const label = order.order_type === "installment" && !(order.payment_stage || "").toLowerCase().startsWith("final")
      ? `Confirm ${order.payment_stage} (awaits next payment)`
      : "Confirm payment";
    actions.push({ label, path: "finance-confirm", icon: <Receipt size={16} weight="bold" />, primary: true });
    actions.push({ label: "Reject payment", path: "finance-reject", icon: <XCircle size={16} weight="bold" />, requireReason: true, danger: true });
  }
  if (isAssignedAdmin && order.status === "payment_confirmed") {
    actions.push({ label: "Dispatch to Warehouse", path: "dispatch", icon: <Truck size={16} weight="bold" />, primary: true });
    actions.push({ label: "Flag refund (product unavailable)", path: "flag-refund", requireReason: true, icon: <WarningCircle size={16} weight="bold" />, danger: true, body: (r) => ({ mode: "product_unavailable", reason: r }) });
    actions.push({ label: "Flag refund (customer paid excess)", path: "flag-refund", requireReason: true, icon: <CurrencyNgn size={16} weight="bold" />, body: (r) => ({ mode: "excess", reason: r }) });
  }
  if (isFinance && order.status === "refund_pending") {
    actions.push({
      label: `Process refund${order.refund_mode === "excess" ? " (excess returned)" : " (full refund)"}`,
      path: "process-refund",
      icon: <ArrowsClockwise size={16} weight="bold" />,
      primary: true,
      requireReason: true,
      body: (r) => ({ note: r, proof_url: null }),
    });
  }
  if (isWarehouseRole && order.status === "in_warehouse") {
    actions.push({ label: "Mark delivered", path: "warehouse-deliver", icon: <Package size={16} weight="bold" />, primary: true });
  }
  if (isAssignedAdmin && order.status === "delivered") {
    actions.push({ label: "Mark completed & notify Sales", path: "complete", icon: <CheckCircle size={16} weight="bold" />, primary: true });
  }
  if (isAssignedAdmin && !["completed", "cancelled", "refund_pending"].includes(order.status)) {
    actions.push({ label: "Cancel order", path: "cancel", icon: <XCircle size={16} weight="bold" />, requireReason: true, danger: true });
  }

  const canSalesEdit = isOwnerSales && ["awaiting_next_payment", "cancelled_by_admin", "pending_admin"].includes(order.status);
  const canAdminEdit = isAssignedAdmin && ["pending_admin", "payment_confirmed"].includes(order.status);

  const pipeline = order.order_type === "installment" ? INSTALLMENT_PIPELINE : STANDARD_PIPELINE;
  const currentIdx = pipeline.findIndex((p) => p.key === order.status);

  return (
    <div className="p-6 md:p-10" data-testid="order-detail-page">
      {changeDialog && (
        <ChangeDialog data={changeDialog} onAck={acknowledgeChange} onDismiss={() => setChangeDialog(null)} />
      )}

      <button onClick={() => navigate(-1)} className="overline inline-flex items-center gap-1 mb-4 hover:text-[var(--solar)]" data-testid="back-btn">
        <ArrowLeft size={14} weight="bold" /> Back
      </button>

      <header className="flex items-start justify-between flex-wrap gap-4 mb-6 pb-6 border-b border-[var(--ink)]">
        <div>
          <div className="overline mb-1">// Order {order.order_type === "installment" && "· Installment"}</div>
          <h1 className="font-display font-black text-4xl tracking-tighter font-mono-tech">{order.order_id}</h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <StatusBadge status={order.status} size="lg" />
            {order.payment_stage && (
              <span className="pill" style={{ color: "#2563EB" }}>{order.payment_stage}</span>
            )}
            <span className="text-xs text-[var(--muted)]">Created {formatDateTime(order.created_at)}</span>
          </div>
        </div>
        {!isWarehouseView && (
          <div className="text-right">
            <div className="overline">Total</div>
            <div className="font-display font-black text-4xl">{formatMoney(order.total_amount)}</div>
            <a
              href={`${API_BASE}/orders/${order.id}/receipt.pdf`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost mt-3 inline-flex items-center gap-2 text-xs"
              data-testid="print-receipt-btn"
            >
              <Printer size={14} weight="bold" /> Print delivery note
            </a>
          </div>
        )}
      </header>

      {/* Pipeline */}
      <div className="mb-8 grid-line bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Pipeline</div>
          {order.order_type === "installment" && (
            <div className="overline text-[var(--solar)]">// Installment loop</div>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {pipeline.map((p, i) => {
            const reached = currentIdx >= i || order.status === "completed";
            const isCurrent = order.status === p.key;
            return (
              <div key={p.key} className="flex items-center min-w-[140px]">
                <div
                  className="flex-1 px-3 py-2 border"
                  style={{
                    borderColor: isCurrent ? "#2563EB" : reached ? "#0F172A" : "#E2E8F0",
                    background: isCurrent ? "#2563EB" : reached ? "#0F172A" : "#fff",
                    color: isCurrent || reached ? "#fff" : "#94A3B8",
                  }}
                >
                  <div className="text-[0.6rem] uppercase tracking-widest font-bold">Step {i + 1}</div>
                  <div className="text-xs font-bold">{p.label}</div>
                </div>
                {i < pipeline.length - 1 && <div className="w-2 h-px bg-[var(--line)]" />}
              </div>
            );
          })}
        </div>
        {(order.status === "cancelled" || order.status === "cancelled_by_admin") && (
          <div className="mt-3 text-xs text-[var(--danger)] font-bold uppercase tracking-widest">
            ! Order halted at {STATUS_META[order.status].label}
          </div>
        )}
        {order.status === "refund_pending" && (
          <div className="mt-3 text-xs text-[var(--danger)] font-bold uppercase tracking-widest">
            ! Refund pending ({order.refund_mode?.replace("_", " ")})
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left col */}
        <div className="lg:col-span-2 space-y-6">
          {!isWarehouseView && (
            <section className="grid-line bg-white">
              <div className="border-b border-[var(--line)] px-5 py-3 font-display font-bold">Customer</div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Info label="Customer / company" value={order.customer.name} />
                <Info label="Phone" value={order.customer.phone} />
                <Info label="Email" value={order.customer.email || "—"} />
                <Info label="Name on payment" value={order.customer.payment_name || "—"} />
                <Info label="Delivery address" value={order.customer.address} full />
              </div>
            </section>
          )}
          {isWarehouseView && (
            <section className="grid-line bg-white">
              <div className="border-b border-[var(--line)] px-5 py-3 font-display font-bold">Delivery details</div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Info label="Company name" value={order.customer.name} />
                <Info label="Phone" value={order.customer.phone} />
                <Info label="Address" value={order.customer.address} full />
              </div>
            </section>
          )}

          <section className="grid-line bg-white">
            <div className="border-b border-[var(--line)] px-5 py-3 flex items-center justify-between">
              <div className="font-display font-bold">Items</div>
              {(canSalesEdit || canAdminEdit) && !editing && (
                <button className="btn-ghost text-xs" onClick={startEdit} data-testid="edit-items-btn">
                  Edit items
                </button>
              )}
            </div>
            {!editing ? (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Product</th>
                      <th>Qty</th>
                      {!isWarehouseView && <th>Unit Price</th>}
                      <th>Truck fee / unit</th>
                      {!isWarehouseView && <th className="text-right">Line total</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((it, i) => (
                      <tr key={i}>
                        <td>{it.category}</td>
                        <td className="font-medium">{it.name}</td>
                        <td>{it.quantity}</td>
                        {!isWarehouseView && <td>{formatMoney(it.unit_price)}</td>}
                        <td>{it.truck_fee ? formatMoney(it.truck_fee) : "—"}</td>
                        {!isWarehouseView && <td className="text-right font-medium">{formatMoney(lineTotal(it))}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!isWarehouseView && (
                  <div className="border-t border-[var(--ink)] flex justify-end p-4">
                    <div>
                      <div className="overline text-right">Total</div>
                      <div className="font-display font-black text-2xl">{formatMoney(order.total_amount)}</div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <EditItemsPanel
                editItems={editItems}
                setEditItems={setEditItems}
                editStage={editStage}
                setEditStage={setEditStage}
                editChangeSummary={editChangeSummary}
                setEditChangeSummary={setEditChangeSummary}
                showStage={order.order_type === "installment"}
                onCancel={() => setEditing(false)}
                onSave={saveEdit}
                saving={actionLoading}
              />
            )}
          </section>

          {!isWarehouseView && (
            <section className="grid-line bg-white">
              <div className="border-b border-[var(--line)] px-5 py-3 font-display font-bold">Activity history</div>
              <ol className="p-5 space-y-3">
                {(order.history || []).slice().reverse().map((h, i) => (
                  <li key={i} className="flex gap-3 border-l-2 border-[var(--solar)] pl-3" data-testid={`history-${i}`}>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {h.actor_name} <span className="text-[var(--muted)]">({ROLE_LABEL[h.actor_role] || h.actor_role})</span> · {h.action.replace(/_/g, " ")}
                      </div>
                      {h.note && <div className="text-xs text-[var(--muted)] mt-0.5">“{h.note}”</div>}
                      <div className="text-[0.7rem] text-[var(--muted)] mt-0.5">{formatDateTime(h.at)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>

        {/* Right col */}
        <aside className="space-y-6">
          <section className="grid-line bg-white">
            <div className="border-b border-[var(--line)] px-5 py-3 font-display font-bold">
              {isWarehouseView ? "Warehouses & delivery" : "Assignments"}
            </div>
            <div className="p-5 space-y-3 text-sm">
              {!isWarehouseView && <Info label="Sales rep" value={order.sales_rep_name} />}
              {!isWarehouseView && <Info label="Assigned admin" value={order.assigned_admin_name} />}
              <Info label="Warehouses" value={(order.warehouses || []).join(" · ") || "—"} />
              {!isWarehouseView && <Info label="Payment method" value={order.payment_method || "—"} />}
              <Info label="Preferred delivery" value={order.delivery_date || "—"} />
              {!isWarehouseView && <Info label="Notes" value={order.notes || "—"} />}
              {order.refund_mode && !isWarehouseView && (
                <div className="border-t border-[var(--line)] pt-3">
                  <div className="overline mb-1 text-[var(--danger)]">Refund</div>
                  <div className="text-sm">
                    Mode: <span className="font-bold">{order.refund_mode.replace("_", " ")}</span>
                  </div>
                  {order.refund_note && <div className="text-xs text-[var(--muted)] mt-1">{order.refund_note}</div>}
                </div>
              )}
              {order.last_action_reason && !isWarehouseView && (
                <div className="border-t border-[var(--line)] pt-3">
                  <div className="overline mb-1 text-[var(--danger)]">Last reason</div>
                  <div className="text-sm">{order.last_action_reason}</div>
                </div>
              )}
            </div>
          </section>

          {actions.length > 0 && !editing && (
            <section className="grid-line bg-white" data-testid="action-panel">
              <div className="border-b border-[var(--line)] px-5 py-3 font-display font-bold">Actions for you</div>
              <div className="p-5 space-y-3">
                <textarea
                  className="textarea-tech"
                  rows={3}
                  placeholder="Reason / note (required for rejections, cancellations, refunds)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="action-reason"
                />
                {actions.map((a, idx) => (
                  <button
                    key={`${a.path}-${idx}`}
                    disabled={actionLoading}
                    onClick={() => callAction(a.path, a.requireReason, a.body ? a.body(reason) : undefined)}
                    className={`w-full inline-flex items-center justify-center gap-2 ${a.primary ? "btn-solar" : a.danger ? "btn-ghost border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white" : "btn-ghost"}`}
                    data-testid={`action-${a.path}-${idx}`}
                  >
                    {a.icon} {a.label}
                  </button>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function Info({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="overline mb-0.5">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function ChangeDialog({ data, onAck, onDismiss }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="change-dialog">
      <div className="bg-white border border-[var(--ink)] max-w-md w-full">
        <div className="bg-[var(--solar)] text-white px-5 py-3 flex items-center gap-2">
          <WarningCircle size={20} weight="fill" />
          <div className="font-display font-bold">Order was updated</div>
        </div>
        <div className="p-5">
          <div className="font-mono-tech text-xs text-[var(--muted)] mb-2">{data.orderRef}</div>
          <p className="text-sm">{data.summary}</p>
        </div>
        <div className="border-t border-[var(--line)] p-4 flex items-center justify-end gap-2">
          <button onClick={onDismiss} className="btn-ghost text-xs" data-testid="change-dismiss">Later</button>
          <button onClick={onAck} className="btn-solar text-xs" data-testid="change-ack">Got it</button>
        </div>
      </div>
    </div>
  );
}

function EditItemsPanel({
  editItems, setEditItems, editStage, setEditStage, editChangeSummary, setEditChangeSummary,
  showStage, onCancel, onSave, saving,
}) {
  const updateItem = (idx, patch) =>
    setEditItems((curr) => curr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () =>
    setEditItems((curr) => [...curr, { category: "Solar Panel", name: "", quantity: 1, unit_price: 0, truck_fee: 0 }]);
  const removeItem = (idx) => setEditItems((curr) => curr.filter((_, i) => i !== idx));

  return (
    <div className="p-5 space-y-3" data-testid="edit-items-panel">
      {showStage && (
        <div className="mb-2">
          <label className="label-tech">Payment stage</label>
          <select className="select-tech" value={editStage} onChange={(e) => setEditStage(e.target.value)} data-testid="edit-stage">
            <option value="">—</option>
            {PAYMENT_STAGES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      )}
      {editItems.map((it, idx) => (
        <div key={idx} className="grid grid-cols-12 gap-2 items-end border border-[var(--line)] p-3">
          <div className="col-span-12 md:col-span-3">
            <label className="label-tech">Category</label>
            <input className="input-tech" value={it.category}
              onChange={(e) => updateItem(idx, { category: e.target.value })} />
          </div>
          <div className="col-span-12 md:col-span-4">
            <label className="label-tech">Product</label>
            <input className="input-tech" value={it.name}
              onChange={(e) => updateItem(idx, { name: e.target.value })} />
          </div>
          <div className="col-span-4 md:col-span-1">
            <label className="label-tech">Qty</label>
            <input type="number" min={1} className="input-tech" value={it.quantity}
              onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
          </div>
          <div className="col-span-4 md:col-span-2">
            <label className="label-tech">Unit</label>
            <input type="number" min={0} step="0.01" className="input-tech" value={it.unit_price}
              onChange={(e) => updateItem(idx, { unit_price: e.target.value })} />
          </div>
          <div className="col-span-4 md:col-span-1">
            <label className="label-tech">Truck</label>
            <input type="number" min={0} step="0.01" className="input-tech" value={it.truck_fee || 0}
              onChange={(e) => updateItem(idx, { truck_fee: e.target.value })} />
          </div>
          <div className="col-span-12 md:col-span-1">
            <button type="button" onClick={() => removeItem(idx)}
              disabled={editItems.length === 1}
              className="border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] p-2 disabled:opacity-30 w-full">
              ✕
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={addItem} className="btn-ghost inline-flex items-center gap-2 text-xs">+ Add item</button>

      <div className="mt-3">
        <label className="label-tech">Optional: short change summary for admin & finance</label>
        <input className="input-tech" value={editChangeSummary}
          onChange={(e) => setEditChangeSummary(e.target.value)}
          placeholder="e.g. 2nd payment received; added 100W panel"
          data-testid="edit-change-summary" />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onCancel} className="btn-ghost text-xs" data-testid="edit-cancel">Cancel</button>
        <button onClick={onSave} disabled={saving} className="btn-solar text-xs" data-testid="edit-save">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
